import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { DocusignStatus } from '~app/shared/enums/docusignStatus.enum';
import { HealthCheck } from '~app/shared/models/healthCheck/healthCheck.model';
import { Policy } from '~app/shared/models/policy/policy.model';
import { Ship } from '~app/shared/models/ship.model';
import { User } from '~app/shared/models/user.model';
import { WorkflowState } from '~app/shared/models/workflow';
import { DataUtilsService } from '../data-utils/data-utils.service';
import { MwService } from '../mw/mw.service';
import { StoreService } from '../store/store.service';
import { AssignmentPolicies, AssignmentContract, Assignment } from '~app/shared/models/assignment';
import { StepperAssignmentType } from '../../types/stepper-assignment-type.type';
import { parseDateFromMW } from '~app/shared/functions/utils/utils.function';
import {
  IGetSignOnPoliciesAcknowledgementResponse,
  IPolicy,
} from '../mw/interfaces/GetSignOnPoliciesAcknowledgement.interface';

@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  constructor(
    private readonly mw: MwService,
    private readonly store: StoreService,
    private readonly dataUtils: DataUtilsService,
  ) {}

  public async loadAssignments(user: User): Promise<void> {
    await this.dataUtils.ensureShipsContextData();

    const assignmentsData = await this.mw.getCrewAssignments(user.id).toPromise();

    user.assignments = assignmentsData.payload.assignments.map(
      data => new Assignment(data, this.store.ships, this.store.ports),
    );
  }

  public async loadWorkflowState(
    assignment: Assignment,
    assignmentType: StepperAssignmentType = 'check-in',
  ): Promise<void> {
    const workflowStateData =
      assignmentType === 'check-out'
        ? await this.mw
            .getCheckOutWorkflowState(assignment.ship.code, assignment.data.signoffDate, assignment.crewMemberId)
            .toPromise()
        : await this.mw
            .getWorkflowState(assignment.ship.code, assignment.data.signonDate, assignment.crewMemberId)
            .toPromise();

    const workflowState = new WorkflowState(workflowStateData.payload);
    assignment.workflowState = workflowState.data;
  }

  public async loadDraftContract(assignment: Assignment): Promise<void> {
    try {
      const content = await this.mw.getSOEAFromEFS(assignment.data.serviceAgreementLink).toPromise();
      assignment.soea = new AssignmentContract(content);
    } catch (error) {
      console.error('Error loading draft contract:', error);
      // Return an empty blob if the request fails
      assignment.soea = new AssignmentContract(new Blob());
    }
  }

  public async loadSignedContract(assignment: Assignment): Promise<void> {
    const content = await this.mw
      .getSOEAFromSharepoint(assignment.crewMemberId, assignment.data.shipCode, assignment.data.signonDate)
      .toPromise();
    assignment.soea = new AssignmentContract(content);
  }

  public async loadDocusignRecipientView(assignment: Assignment): Promise<void> {
    const recipientViewURL = await this.mw
      .getDocusignRecipientView({
        crewMemberId: assignment.crewMemberId,
        crewMemberName: `${assignment.data.firstName} ${assignment.data.lastName}`,
        docusignStatus: assignment.data.docusignStatus,
        envelopeId: assignment.envelopeId,
        shipCode: assignment.ship.code,
        signonDate: assignment.data.signonDate,
      })
      .toPromise();
    assignment.data.docusignViews.recipient = recipientViewURL.payload.url;
  }

  public async loadDocusignConsoleView(assignments: Assignment[], hrManagerCrewMemberId: string): Promise<string> {
    const res = await this.mw
      .getDocusignConsoleView({
        hrManagerCrewMemberId,
        crewMemberDetails: [
          ...assignments.map(assignment => ({
            crewMemberId: assignment.crewMemberId,
            envelopeId: assignment.envelopeId,
            signonDate: assignment.data.signonDate,
            docusignStatus: assignment.data.docusignStatus,
          })),
        ],
        shipCode: assignments[0].ship.code,
      })
      .toPromise();
    return res.payload.url;
  }

  public async loadSignedPolicies(assignment: Assignment, user?: User): Promise<void> {
    const policiesData = await this.mw
      .getSignOnPoliciesAcknowledgement(assignment?.ship?.code, assignment.data.signonDate, assignment.crewMemberId)
      .toPromise();
    if (user) {
      this.insertSignedDateIntoPolicies('check-in', policiesData, user);
    }
    if (assignment.policies && assignment.policies?.policies?.length) {
      policiesData.payload.policies = [...(policiesData.payload?.policies || []), ...assignment.policies?.policies];
    }
    assignment.policies = new AssignmentPolicies(policiesData.payload);
  }

  public async loadSignedDebarkPolicies(assignment: Assignment, user?: User): Promise<void> {
    const policiesData = await this.mw
      .getSignOffPoliciesAcknowledgement(assignment?.ship?.code, assignment.data.signoffDate, assignment.crewMemberId)
      .toPromise();
    if (user) {
      this.insertSignedDateIntoPolicies('check-out', policiesData, user);
    }
    if (assignment.policies && assignment.policies?.policies?.length) {
      policiesData.payload.policies = [...(policiesData.payload?.policies || []), ...assignment.policies?.policies];
    }
    assignment.policies = new AssignmentPolicies(policiesData.payload);
  }

  public insertSignedDateIntoPolicies(
    flow: 'check-in' | 'check-out',
    response: IGetSignOnPoliciesAcknowledgementResponse,
    user: User,
  ): void {
    const timestamp = response.payload?.timestamp?.substring(0, 8);
    const policiesWithSignedDate =
      response.payload?.policies?.map((policy: IPolicy) => ({
        ...policy,
        signedDate: new Date(timestamp ? parseDateFromMW(timestamp) : undefined),
      })) || [];
    if (flow === 'check-out') {
      user.signedCheckOutPolicies = policiesWithSignedDate;
    } else {
      user.signedCheckInPolicies = policiesWithSignedDate;
    }
  }

  public async loadHealthCheck(assignment: Assignment): Promise<void> {
    await this.dataUtils.ensureSymptomsAndQuestions();
    const healthCheckData = await this.mw
      .getHealthCheckSurvey(assignment.ship.code, assignment.data.signonDate, assignment.crewMemberId)
      .toPromise();
    assignment.healthCheck = new HealthCheck(healthCheckData.payload, this.store.symptoms, this.store.questions);
  }

  public async getEnhancedSignonAssignments(ship: Ship, startDate: Date, endDate?: Date): Promise<Assignment[]> {
    await this.dataUtils.ensureShipsContextData();

    const parsedStartDate = format(startDate, 'yyyyMMdd');
    let parsedEndDate;
    if (endDate) {
      parsedEndDate = format(endDate, 'yyyyMMdd');
    } else {
      parsedEndDate = parsedStartDate;
    }

    const assignmentsData = await this.mw.getEnhancedShipSignons(ship.code, parsedStartDate, parsedEndDate).toPromise();
    if (assignmentsData.payload) {
      return assignmentsData.payload
        .flatMap(signonAssignment =>
          signonAssignment.signonAssignments.map(assignment => ({
            ...assignment,
            signonDate: signonAssignment.signonDate,
            shipCode: signonAssignment.shipCode,
          })),
        )
        .map(v => {
          const assignment = new Assignment(v, this.store.ships, this.store.ports);
          assignment.workflowState = v.workflowStates;
          return assignment;
        });
    } else {
      return [];
    }
  }

  public async getShipSignoffs(ship: Ship, endDate: Date): Promise<Assignment[]> {
    await this.dataUtils.ensureShipsContextData();

    const parsedEndDate = format(endDate, 'yyyyMMdd');

    const assignmentsData = await this.mw.getShipSignoffs(ship.code, parsedEndDate).toPromise();
    if (assignmentsData.payload) {
      return assignmentsData.payload
        .flatMap(signoffAssignment =>
          signoffAssignment.signoffAssignments.map(assignment => ({
            ...assignment,
            signoffDate: signoffAssignment.signoffDate,
            shipCode: signoffAssignment.shipCode,
          })),
        )
        .map(v => {
          const assignment = new Assignment(v, this.store.ships, this.store.ports);
          assignment.workflowState = { debarkStatus: v.debarkStatus };
          return assignment;
        });
    }
    return [];
  }

  public async getUserAssignments(ship: Ship, crewMemberId: string): Promise<Assignment[]> {
    await this.dataUtils.ensureShipsContextData();
    const assignmentsData = await this.mw.getAssignments(ship.code, crewMemberId).toPromise();
    return assignmentsData.payload.assignments.map(v => new Assignment(v, this.store.ships, this.store.ports));
  }

  public async updateCheckinProgress(assignment: Assignment, progress: number): Promise<void> {
    await this.mw
      .updateWorkflowState(
        {
          precheckStatus: progress,
        },
        assignment.data.shipCode,
        assignment.data.signonDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.precheckStatus = progress;
  }

  public async updateCheckoutProgress(assignment: Assignment, progress: number): Promise<void> {
    await this.mw
      .updateDebarkWorkflowState(
        {
          debarkStatus: progress,
        },
        assignment.data.shipCode,
        assignment.data.signoffDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.debarkStatus = progress;
  }

  public async updateHealthCheck(assignment: Assignment, hasSymptoms: boolean): Promise<void> {
    await this.mw
      .updateHealthCheckSurvey(assignment.data.shipCode, assignment.data.signonDate, hasSymptoms)
      .toPromise();
    assignment.healthCheck.data.hasSymptom = hasSymptoms;
  }

  public async completeHealthCheck(assignment: Assignment): Promise<void> {
    const healthCheckStatus = assignment.healthCheck.hasSymptom ? 2 : 1;
    await this.mw
      .updateWorkflowState(
        {
          healthCheckStatus,
        },
        assignment.data.shipCode,
        assignment.data.signonDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.healthCheckStatus = healthCheckStatus;
  }

  public async medicalApprove(assignment: Assignment): Promise<void> {
    await this.mw
      .updateWorkflowState(
        {
          medicalStatus: 100,
        },
        assignment.data.shipCode,
        assignment.data.signonDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.medicalStatus = 100;
  }

  public async approveCertificate(assignment: Assignment): Promise<void> {
    await this.mw
      .updateWorkflowState(
        {
          safetyStatus: 100,
        },
        assignment.data.shipCode,
        assignment.data.signonDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.safetyStatus = 100; // update model locally
  }

  public async acceptDocuments(assignment: Assignment): Promise<void> {
    await this.mw
      .updateWorkflowState(
        {
          immigrationStatus: 100,
        },
        assignment.data.shipCode,
        assignment.data.signonDate,
        assignment.crewMemberId,
      )
      .toPromise();
    assignment.workflowState.data.immigrationStatus = 100;
  }

  public async releaseMultipleContracts(assignments: Assignment[], reason?: string): Promise<void> {
    const envelopeIds = assignments.map(assignment => assignment.envelopeId);
    await this.mw.deleteMultipleWatermarks(envelopeIds, reason).toPromise();
    assignments.forEach(assignment => (assignment.data.docusignStatus.envelopeStatus = DocusignStatus.Completed));
  }

  public async deactivateAssignment(assignment: Assignment): Promise<void> {
    await this.mw
      .activateAssignment(assignment.crewMemberId, assignment.data.shipCode, assignment.data.signonDate, false)
      .toPromise();
  }

  public async rateExperience(assignment: Assignment, rating: number): Promise<void> {
    await this.mw.rateCheckinExperience(assignment.data.shipCode, assignment.data.signonDate, rating).toPromise();
  }

  public async signPolicies(assignment: Assignment, policies: Policy[]): Promise<void> {
    await this.mw
      .updateSignOnPoliciesAcknowledgement(
        assignment.data.shipCode,
        assignment.data.signonDate,
        policies.map(policy => ({
          policyIdentifier: policy.id,
          contentVersion: policy.version,
        })),
      )
      .toPromise();
  }

  public async signDebarkPolicies(assignment: Assignment, policies: Policy[]): Promise<void> {
    await this.mw
      .updateSignOffPoliciesAcknowledgement(
        assignment.data.shipCode,
        assignment.data.signoffDate,
        policies.map(policy => ({
          policyIdentifier: policy.id,
          contentVersion: policy.version,
        })),
      )
      .toPromise();
  }
}
